import { supabase } from '../lib/supabase'
import { getCurrentUserId } from '../lib/auth'
import { logTenancyMessageSent } from '../lib/audit'
import type { TenancyMessage, MessageSenderRole } from '../types/messages'

/**
 * Fetch messages for a tenancy, oldest-first.
 * Joins sender profile for display name.
 */
export async function getMessages(
  tenancyId: string,
  limit = 50,
  before?: string
): Promise<TenancyMessage[]> {
  await getCurrentUserId() // auth guard — RLS enforces data isolation

  let query = supabase
    .from('tenancy_messages')
    .select('*, sender:profiles!tenancy_messages_sender_id_fkey(full_name, role)')
    .eq('tenancy_id', tenancyId)
    .order('created_at', { ascending: true })
    .limit(limit)

  if (before) {
    query = query.lt('created_at', before)
  }

  const { data, error } = await query
  if (error) throw error
  return data as TenancyMessage[]
}

/**
 * Send a message in a tenancy conversation.
 * Logs audit trail entry and notifies the other party via digest.
 */
export async function sendMessage(
  tenancyId: string,
  body: string,
  senderRole: MessageSenderRole
): Promise<TenancyMessage> {
  const userId = await getCurrentUserId()

  const { data, error } = await supabase
    .from('tenancy_messages')
    .insert({
      tenancy_id: tenancyId,
      sender_id: userId,
      sender_role: senderRole,
      body: body.trim(),
    })
    .select('*, sender:profiles!tenancy_messages_sender_id_fkey(full_name, role)')
    .single()

  if (error) throw error

  const message = data as TenancyMessage

  // Audit trail — append-only, server-generated timestamp
  await logTenancyMessageSent(message.id, tenancyId, {
    sender_id: userId,
    sender_role: senderRole,
  })

  // Notifications are handled by the database trigger (trg_tenancy_message_notification)
  // which fires on INSERT and creates bell notifications server-side via SECURITY DEFINER.

  return message
}

/**
 * Mark all unread messages (not sent by current user) as read.
 * Uses the direct policy-based update (covers owner, team, and tenant).
 *
 * NOTE: mark_messages_read RPC exists (migration 356) but is tenant-only.
 * This function stays on the direct update until the tenancy migration adds
 * check_tenancy_internal_access, at which point the RPC can cover all three
 * actor types and replace this direct update.
 */
export async function markAsRead(tenancyId: string): Promise<void> {
  const userId = await getCurrentUserId()

  const { error } = await supabase
    .from('tenancy_messages')
    .update({ read_at: new Date().toISOString() })
    .eq('tenancy_id', tenancyId)
    .neq('sender_id', userId)
    .is('read_at', null)

  if (error) throw error
}

/**
 * Count unread messages for the current user on a tenancy.
 */
export async function getUnreadCount(tenancyId: string): Promise<number> {
  const userId = await getCurrentUserId()

  const { count, error } = await supabase
    .from('tenancy_messages')
    .select('id', { count: 'exact', head: true })
    .eq('tenancy_id', tenancyId)
    .neq('sender_id', userId)
    .is('read_at', null)

  if (error) throw error
  return count ?? 0
}

/**
 * Count all unread messages for the current user across all tenancies.
 * Used for sidebar badge — no tenancy ID needed.
 */
export async function getMyUnreadMessageCount(): Promise<number> {
  const userId = await getCurrentUserId()

  const { count, error } = await supabase
    .from('tenancy_messages')
    .select('id', { count: 'exact', head: true })
    .neq('sender_id', userId)
    .is('read_at', null)

  if (error) throw error
  return count ?? 0
}

